import type { ErrorMsg } from '@shared/protocol/messages';
import type { App } from '../app/App';
import { t } from '../i18n';

/**
 * Quick battles: once two friends are in a public room on purpose (one
 * invited or shared the room, the other joined it), the battle starts by
 * itself. The host's game sends the challenge as soon as the friend arrives,
 * and the friend's game accepts it, so nobody presses Challenge / Accept.
 * Classroom rooms keep the manual buttons: a teacher may want to wait.
 *
 * Works on every screen (the host may wait on another page).
 */
export class QuickFight {
  /** Code of the room this player made or joined for a quick battle; '' = any room I am about to get. */
  private room: string | null = null;
  private challengedFor = '';

  constructor(private readonly app: App) {
    app.room.on('room', (r) => {
      if (!r) {
        this.room = null;
        this.challengedFor = '';
        return;
      }
      if (this.room === '') this.room = r.code;
      if (this.room !== r.code || r.mode !== 'public') return;
      const me = app.room.playerId;
      const friend = r.players.find((p) => p.id !== me);
      // Host: the friend just arrived and nobody has challenged yet.
      if (r.status === 'lobby' && r.hostId === me && friend?.connected && r.challengerId === null && this.challengedFor !== `${r.code}:${friend.id}`) {
        this.challengedFor = `${r.code}:${friend.id}`;
        app.mp.send({ t: 'challenge.send' }).catch((e: ErrorMsg) => app.toast(e.message ?? t('Something went wrong'), 'error'));
      }
    });
    app.room.on('challenge', () => {
      const r = app.room.room;
      // Friend: I joined this room to fight, so the challenge is a yes.
      if (!r || this.room !== r.code || r.mode !== 'public' || r.hostId === app.room.playerId) return;
      app.mp.send({ t: 'challenge.respond', accept: true }).catch((e: ErrorMsg) => app.toast(e.message ?? t('Something went wrong'), 'error'));
    });
  }

  /** The next room this player gets (made, joined or invited into) starts its battle by itself. */
  armNext(): void {
    this.room = '';
    this.challengedFor = '';
  }

  /** The room this player is already in starts by itself when a friend arrives. */
  armCurrent(): void {
    this.room = this.app.room.room?.code ?? '';
  }

  /** True when this room will start by itself (the screen hides the Challenge button). */
  isQuick(code: string): boolean {
    return this.room === code;
  }
}

/** The address that opens the game straight into a room: https://paper-legends.com/?room=ABC123 */
export function roomLink(code: string): string {
  const url = new URL(location.href);
  url.search = '';
  url.hash = '';
  url.searchParams.set('room', code);
  return url.toString();
}

/** A room code from the address bar (?room=ABC123), removed from it so a reload does not join again. */
export function takeRoomFromUrl(): string | null {
  const url = new URL(location.href);
  const code = (url.searchParams.get('room') ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (!url.searchParams.has('room')) return null;
  url.searchParams.delete('room');
  history.replaceState(null, '', url.pathname + url.search + url.hash);
  return code.length === 6 ? code : null;
}

/** Shares the room link (the phone's share sheet when there is one, else the clipboard). */
export async function shareRoom(app: App, code: string): Promise<void> {
  const link = roomLink(code);
  const text = t('Battle me in Paper Legends! Room {code}', { code });
  try {
    if (navigator.share) {
      await navigator.share({ title: 'Paper Legends', text, url: link });
      return;
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') return; // the player closed the share sheet
  }
  try {
    await navigator.clipboard.writeText(`${text} ${link}`);
    app.toast(t('Link copied! Send it to your friend.'), 'success');
  } catch {
    app.toast(t('Could not copy. Tell your friend the code {code} instead.', { code }), 'info');
  }
}
